import LiteYouTubeEmbed from "react-lite-youtube-embed";
import { graphql, useRefetchableFragment } from "react-relay";
import { Image } from "@/components/image";
import type { postCoverFragment$key } from "./__generated__/postCoverFragment.graphql";
import "react-lite-youtube-embed/dist/LiteYouTubeEmbed.css";
import { Icon } from "@/components/icon";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { useColors } from "@/hooks/useColors";
import { ImageFullscreen } from "./image-fullscreen";
import type { PostCoverContentQuery } from "./__generated__/PostCoverContentQuery.graphql";
import { lazy, Suspense, useEffect } from "react";

const MuxPlayer = lazy(() => import("@/components/lazy-mux-player"));

type PostCoverProps = {
	post: postCoverFragment$key;
};

export function PostCover({ post }: PostCoverProps) {
	const { primary, accent, secondary } = useColors();
	const [data, refetch] = useRefetchableFragment<
		PostCoverContentQuery,
		postCoverFragment$key
	>(
		graphql`
    fragment postCoverFragment on Post @refetchable(queryName: "PostCoverContentQuery") {
        coverContent {
            ... on ExternalVideoCover {
                url
            }

            ... on ImageFile {
                id
                rawID
                name
                signedUrl
            }

            ... on VideoFile {
                id
                rawID
                muxId
                url
                status
                thumbnail {
                  signedUrl
                }
                playback {
                  playback
                  tokens {
                    playback
                    storyboard
                    thumbnail
                  }
                }
            }

            __typename
        }
    }
  `,
		post,
	);

	useEffect(() => {
		if (data.coverContent?.__typename === "VideoFile") {
			if (data.coverContent.status === "PROCESSING") {
				const timeout = setTimeout(() => {
					refetch({}, { fetchPolicy: "network-only" });
				}, 10000);

				return () => {
					clearTimeout(timeout);
				};
			}
		}
	}, [data.coverContent, refetch]);

	if (!data.coverContent) {
		return null;
	}

	if (data.coverContent.__typename === "ExternalVideoCover") {
		if (!data.coverContent?.url) {
			return null;
		}

		if (data.coverContent.url?.includes("youtube.com")) {
			const regExp =
				/^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;

			const match = data.coverContent.url.match(regExp);

			if (match) {
				return (
					<div className="aspect-video w-full overflow-clip">
						<LiteYouTubeEmbed
							id={match[7]}
							title=""
							poster="maxresdefault"
							webp
						/>
					</div>
				);
			}
		}

		return (
			<div className="aspect-video w-full overflow-clip">
				<iframe
					title="youtube-video"
					src={data.coverContent?.url}
					className="w-full object-cover"
				/>
			</div>
		);
	}

	if (data.coverContent.__typename === "VideoFile") {
		if (data.coverContent.status === "PROCESSING") {
			return (
				<div
					className="flex w-full flex-col items-center justify-center gap-3 overflow-x-hidden bg-zinc-950/10 font-medium text-muted-foreground text-sm dark:bg-white/10"
					style={{ aspectRatio: 16 / 9 }}
				>
					<Spinner />
					<span>
						Vídeo em processamento. Ele estará disponível em alguns minutos.
					</span>
				</div>
			);
		}

		if (data.coverContent.status === "ERROR") {
			return (
				<div
					className="flex w-full flex-col items-center justify-center gap-3 overflow-x-hidden bg-red-100 font-medium text-red-800 text-xs"
					style={{ aspectRatio: 16 / 9 }}
				>
					<Icon name="CircleAlert" className="size-5" />
					Vídeo Indisponível. Entre em contato com o administrador.
				</div>
			);
		}

		return (
			<div className="aspect-video w-full overflow-clip">
				<Suspense
					fallback={
						<div className="aspect-video w-full bg-zinc-950/10 dark:bg-white/10 animate-pulse" />
					}
				>
					<MuxPlayer
						streamType="on-demand"
						className="aspect-video rounded-md"
						playbackId={data.coverContent.playback?.playback}
						tokens={{
							playback:
								data.coverContent.playback?.tokens?.playback || undefined,
							storyboard:
								data.coverContent.playback?.tokens?.storyboard || undefined,
							thumbnail:
								data.coverContent.playback?.tokens?.thumbnail || undefined,
						}}
						style={{ aspectRatio: 16 / 9 }}
						{...(data.coverContent?.thumbnail?.signedUrl
							? { poster: data.coverContent.thumbnail.signedUrl }
							: {})}
						accentColor={primary}
						secondaryColor="transparent"
					/>
				</Suspense>
			</div>
		);
	}

	if (data.coverContent.__typename === "ImageFile") {
		return (
			<Dialog>
				<DialogTrigger asChild>
					<Image
						src={data.coverContent.signedUrl}
						className="size-full aspect-video object-cover mb-0"
						alt={data.coverContent.name}
						width={800}
					/>
				</DialogTrigger>

				<ImageFullscreen src={data.coverContent.signedUrl} />
			</Dialog>
		);
	}

	return null;
}
